import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DetailPanel from "./DetailPanel";
import GraphView from "./GraphView";
import ShareDialog from "./ShareDialog";
import WeightPanel from "./WeightPanel";
import {
  FACTORS,
  TAG_KINDS,
  buildGraph,
  fitView,
  initialPositions,
  loadModel,
  step,
  strongestPath,
} from "./graphModel";
import { clusterStats, resolveUserClusters, suggestForCluster, useUserClusters } from "./userClusters";
import "./Ludograph.scss";

const NARROW_WIDTH = 1100;
const DEFAULT_THRESHOLD = 15;
const UNCLUSTERED_KEY = "ludograph.onlyUnclustered";
const SUGGEST_OTHERS_KEY = "ludograph.suggestFromOtherClusters";
const HISTORY_LIMIT = 50;

const isEmptyView = (v) =>
  v.sel == null && v.cluster == null && v.userCluster == null && !v.tagFilter.length;

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

// Factor order in links shared before Difficulty was added; it reads as 0.
const LEGACY_SHARE_KEYS = ["theme", "genre", "mechanics", "publisher", "other"];

// Reads `#/ludograph?w=38-26-17-8-6-5&s=70&min=15` so shared links restore settings.
function readShareParams() {
  const query = window.location.hash.split("?")[1];
  if (!query) return {};
  const params = new URLSearchParams(query);
  const out = {};
  const w = (params.get("w") || "").split("-").map(Number);
  if (w.every((x) => x >= 0 && x <= 100)) {
    if (w.length === FACTORS.length) {
      out.weights = Object.fromEntries(FACTORS.map(({ key }, i) => [key, w[i]]));
    } else if (w.length === LEGACY_SHARE_KEYS.length) {
      out.weights = { difficulty: 0, ...Object.fromEntries(LEGACY_SHARE_KEYS.map((key, i) => [key, w[i]])) };
    }
  }
  const s = Number(params.get("s"));
  if (params.has("s") && s >= 0 && s <= 100) out.seriesWeight = s;
  const min = Number(params.get("min"));
  if (min >= 5 && min <= 80) out.threshold = min;
  return out;
}

const drawerWidth = (narrow, open) => (narrow && open ? Math.min(340, window.innerWidth * 0.6) : 0);

const Ludograph = ({ maxLinks = 7, labels = "major", nodeShape = "square" }) => {
  const shared = useMemo(readShareParams, []);
  const [model, setModel] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [weights, setWeights] = useState(shared.weights || null);
  const [seriesWeight, setSeriesWeight] = useState(shared.seriesWeight ?? null);
  const [threshold, setThreshold] = useState(shared.threshold ?? DEFAULT_THRESHOLD);
  const [sel, setSel] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [hov, setHov] = useState(null);
  const [picking, setPicking] = useState(false);
  const [cluster, setCluster] = useState(null);
  const [userCluster, setUserCluster] = useState(null);
  // Ad hoc filter: games carrying every one of these { kind, value } tags.
  const [tagFilter, setTagFilter] = useState([]);
  // Detail-view history, separate from the browser's: each entry is one
  // selection (game, comparison, cluster or tag filter). Empty views aren't
  // recorded, so Back from an empty view reopens the last entry.
  const [history, setHistory] = useState({ entries: [], index: -1 });
  const [onlyUnclustered, setOnlyUnclustered] = useState(() => readFlag(UNCLUSTERED_KEY));
  const [suggestFromOthers, setSuggestFromOthers] = useState(() => readFlag(SUGGEST_OTHERS_KEY));
  const userClusterStore = useUserClusters();
  const [query, setQuery] = useState("");
  const [activeResult, setActiveResult] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [narrow, setNarrow] = useState(window.innerWidth < NARROW_WIDTH);
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);

  const boxRef = useRef(null);
  const svgRef = useRef(null);
  // Mutable simulation state lives outside React state so the animation
  // loop can update it every frame without re-creating handlers.
  const sim = useRef({
    pos: null,
    alpha: 1,
    view: { k: 1, x: 0, y: 0 },
    size: { w: 900, h: 640 },
    autoFit: true,
    drag: null,
    pan: null,
  }).current;
  const live = useRef({});

  useEffect(() => {
    Promise.all([
      fetch("gameData/similarity.json").then((r) => r.json()),
      fetch("gameData/similarityComponents.json").then((r) => r.json()),
    ])
      .then(([similarity, components]) => {
        const m = loadModel(similarity, components);
        setWeights((w) => w || m.defaultWeights);
        setSeriesWeight((s) => s ?? m.defaultSeriesWeight);
        setModel(m);
      })
      .catch((err) => setLoadError(err.message));
  }, []);

  const graph = useMemo(
    () =>
      model && weights && seriesWeight != null
        ? buildGraph(model, weights, threshold / 100, seriesWeight, maxLinks)
        : null,
    [model, weights, threshold, seriesWeight, maxLinks]
  );
  const userClusters = useMemo(
    () => (model ? resolveUserClusters(userClusterStore.clusters, model) : null),
    [model, userClusterStore.clusters]
  );
  const activeUserCluster = userClusters && userCluster ? userClusters.byId[userCluster] || null : null;
  const suggestions = useMemo(
    () =>
      activeUserCluster && weights
        ? suggestForCluster(model, activeUserCluster.members, userClusters.clusterOf, weights, seriesWeight, {
            includeOtherClusters: suggestFromOthers,
          })
        : [],
    [model, activeUserCluster, userClusters, weights, seriesWeight, suggestFromOthers]
  );
  const userClusterStats = useMemo(
    () =>
      activeUserCluster && weights && graph
        ? clusterStats(model, graph, activeUserCluster.members, weights, seriesWeight)
        : null,
    [model, graph, activeUserCluster, weights, seriesWeight]
  );
  // Games filtered out of the graph: everything already in a user cluster,
  // except the members of the cluster being viewed and games suggested for it.
  const hidden = useMemo(() => {
    if (!onlyUnclustered || !userClusters) return null;
    const set = new Set();
    userClusters.clusterOf.forEach((c, i) => {
      if (c != null && c !== userCluster) set.add(i);
    });
    suggestions.forEach(({ i }) => set.delete(i));
    return set;
  }, [onlyUnclustered, userClusters, userCluster, suggestions]);

  // Every genre, theme and mechanic in the collection, for search.
  const allTags = useMemo(() => {
    if (!model) return [];
    return TAG_KINDS.flatMap(({ kind, single, format }) => {
      const counts = {};
      model.games.forEach((g) => g[kind].forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
      return Object.entries(counts).map(([value, count]) => ({
        kind,
        value,
        count,
        single,
        label: format(value),
        search: format(value).toLowerCase(),
      }));
    });
  }, [model]);

  const tagMatches = useMemo(() => {
    if (!model || !tagFilter.length) return null;
    return model.games
      .filter((g) => (!hidden || !hidden.has(g.index)) && tagFilter.every(({ kind, value }) => g[kind].includes(value)))
      .map((g) => g.index);
  }, [model, tagFilter, hidden]);

  useEffect(() => {
    try {
      window.localStorage.setItem(UNCLUSTERED_KEY, onlyUnclustered ? "1" : "0");
    } catch {
      // Ignore unavailable storage.
    }
  }, [onlyUnclustered]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SUGGEST_OTHERS_KEY, suggestFromOthers ? "1" : "0");
    } catch {
      // Ignore unavailable storage.
    }
  }, [suggestFromOthers]);

  useEffect(() => {
    const view = { sel, cmp, cluster, userCluster, tagFilter };
    if (isEmptyView(view)) return;
    const key = JSON.stringify(view);
    setHistory((h) => {
      if (h.entries[h.index]?.key === key) return h;
      const entries = [...h.entries.slice(0, h.index + 1), { key, view }].slice(-HISTORY_LIMIT);
      return { entries, index: entries.length - 1 };
    });
  }, [sel, cmp, cluster, userCluster, tagFilter]);

  // Drop the selection if its user cluster was deleted (here or in another tab).
  useEffect(() => {
    if (userCluster && userClusters && !userClusters.byId[userCluster]) setUserCluster(null);
  }, [userCluster, userClusters]);

  live.current = {
    graph,
    narrow,
    drawerOpen: sel != null || cluster != null || userCluster != null || tagFilter.length > 0,
  };

  // Settle the layout before the first paint so nodes don't start piled up.
  if (graph && !sim.pos) {
    sim.pos = initialPositions(model.n);
    for (let i = 0; i < 160; i++) step(sim, graph, -1);
    sim.view = fitView(sim, graph, 0);
    sim.settledGraph = graph;
  }

  // Let the layout re-settle when the links change.
  useEffect(() => {
    if (graph && graph !== sim.settledGraph) sim.alpha = Math.max(sim.alpha, 0.55);
  }, [graph, sim]);

  const ready = Boolean(graph);

  useEffect(() => {
    if (!ready) return undefined;
    let raf;
    const loop = () => {
      const { graph: g, narrow: nw, drawerOpen } = live.current;
      let dirty = false;
      if (sim.alpha > 0.004 || sim.drag) {
        const di = sim.drag ? sim.drag.i : -1;
        step(sim, g, di);
        step(sim, g, di);
        dirty = true;
      }
      if (sim.autoFit) {
        const t = fitView(sim, g, drawerWidth(nw, drawerOpen));
        const v = sim.view;
        if (Math.abs(t.k - v.k) > 0.001 || Math.abs(t.x - v.x) > 0.3 || Math.abs(t.y - v.y) > 0.3) {
          v.k += (t.k - v.k) * 0.12;
          v.x += (t.x - v.x) * 0.12;
          v.y += (t.y - v.y) * 0.12;
          dirty = true;
        }
      }
      if (dirty) rerender();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [ready, sim, rerender]);

  useEffect(() => {
    const box = boxRef.current;
    if (!ready || !box) return undefined;
    const measure = () => {
      const rc = box.getBoundingClientRect();
      if (rc.width) {
        sim.size = { w: rc.width, h: rc.height };
        rerender();
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    const onWheel = (e) => {
      e.preventDefault();
      sim.autoFit = false;
      const v = sim.view;
      const rc = box.getBoundingClientRect();
      const sx = e.clientX - rc.left;
      const sy = e.clientY - rc.top;
      const gx = (sx - rc.width / 2) / v.k - v.x;
      const gy = (sy - rc.height / 2) / v.k - v.y;
      const k = Math.min(6, Math.max(0.2, v.k * Math.exp(-e.deltaY * 0.0015)));
      v.k = k;
      v.x = (sx - rc.width / 2) / k - gx;
      v.y = (sy - rc.height / 2) / k - gy;
      rerender();
    };
    box.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      ro.disconnect();
      box.removeEventListener("wheel", onWheel);
    };
  }, [ready, sim, rerender]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW_WIDTH);
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setPicking(false);
      setSharing(false);
      setSel(null);
      setCmp(null);
      setCluster(null);
      setUserCluster(null);
      setTagFilter([]);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const path = graph && sel != null && cmp != null ? strongestPath(graph, model.n, sel, cmp) : null;

  // On narrow screens the detail drawer covers the graph, so frame the
  // selection in the space that is left.
  useEffect(() => {
    if (!graph || !narrow || sel == null || !sim.pos) return;
    const dw = drawerWidth(narrow, true);
    const { w, h } = sim.size;
    const P = sim.pos;
    const r = graph.radius;
    const set = cmp != null ? strongestPath(graph, model.n, sel, cmp) || [sel, cmp] : [sel, ...graph.adj[sel].map((nb) => nb.o)];
    let x0 = 1e9;
    let x1 = -1e9;
    let y0 = 1e9;
    let y1 = -1e9;
    set.forEach((i) => {
      x0 = Math.min(x0, P[i].x - r[i]);
      x1 = Math.max(x1, P[i].x + r[i]);
      y0 = Math.min(y0, P[i].y - r[i]);
      y1 = Math.max(y1, P[i].y + r[i]);
    });
    const labelWidth = 150;
    const k = Math.max(0.3, Math.min(1.6, (w - dw - 60 - labelWidth) / Math.max(1, x1 - x0), (h - 120) / Math.max(1, y1 - y0)));
    sim.autoFit = false;
    sim.view.k = k;
    const sx0 = 30;
    const sx1 = w - dw - 30 - labelWidth;
    sim.view.x = ((sx0 + sx1) / 2 - w / 2) / k - (x0 + x1) / 2;
    sim.view.y = -(y0 + y1) / 2;
    rerender();
    // Only re-frame when the selection or layout mode changes, not every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrow, sel, cmp]);

  if (loadError) return <div className="ludograph ludograph-message">Couldn't load similarity data: {loadError}</div>;
  if (!graph) return <div className="ludograph ludograph-message">Loading…</div>;

  const { games, n } = model;
  const totalWeight = FACTORS.reduce((sum, { key }) => sum + weights[key], 0) || 1;
  const shares = Object.fromEntries(FACTORS.map(({ key }) => [key, Math.round((weights[key] / totalWeight) * 100)]));

  const select = (i) => {
    setSel(i);
    setCmp(null);
    setCluster(null);
    setUserCluster(null);
    setTagFilter([]);
    setHov(null);
    setPicking(false);
  };
  const pick = (i) => {
    if (picking && sel != null) {
      if (i !== sel) {
        setCmp(i);
        setPicking(false);
        setHov(null);
      }
    } else {
      setSel(i);
      setCmp(null);
      setCluster(null);
      setUserCluster(null);
      setTagFilter([]);
    }
  };
  const clearSel = () => {
    setSel(null);
    setCmp(null);
    setPicking(false);
  };
  const focusOn = (i) => {
    sim.autoFit = false;
    sim.view.x = -sim.pos[i].x;
    sim.view.y = -sim.pos[i].y;
    sim.view.k = Math.max(sim.view.k, 1.4);
  };
  const zoom = (f) => {
    sim.autoFit = false;
    sim.view.k = Math.min(6, Math.max(0.2, sim.view.k * f));
    rerender();
  };

  const toGraph = (ev) => {
    const rc = svgRef.current.getBoundingClientRect();
    const v = sim.view;
    return {
      x: (ev.clientX - rc.left - rc.width / 2) / v.k - v.x,
      y: (ev.clientY - rc.top - rc.height / 2) / v.k - v.y,
    };
  };
  const onNodeDown = (i, ev) => {
    ev.stopPropagation();
    sim.drag = { i, sx: ev.clientX, sy: ev.clientY, moved: false };
    svgRef.current.setPointerCapture(ev.pointerId);
  };
  const onBgDown = (ev) => {
    sim.pan = { sx: ev.clientX, sy: ev.clientY, x0: sim.view.x, y0: sim.view.y, moved: false };
    ev.currentTarget.setPointerCapture(ev.pointerId);
  };
  const onMove = (ev) => {
    if (sim.drag) {
      const d = sim.drag;
      if (Math.abs(ev.clientX - d.sx) + Math.abs(ev.clientY - d.sy) > 4) d.moved = true;
      if (d.moved) {
        const p = toGraph(ev);
        sim.pos[d.i].x = p.x;
        sim.pos[d.i].y = p.y;
        sim.autoFit = false;
        sim.alpha = Math.max(sim.alpha, 0.2);
      }
    } else if (sim.pan) {
      const p = sim.pan;
      if (Math.abs(ev.clientX - p.sx) + Math.abs(ev.clientY - p.sy) > 3) p.moved = true;
      if (p.moved) {
        sim.autoFit = false;
        sim.view.x = p.x0 + (ev.clientX - p.sx) / sim.view.k;
        sim.view.y = p.y0 + (ev.clientY - p.sy) / sim.view.k;
        rerender();
      }
    }
  };
  const onUp = () => {
    if (sim.drag && !sim.drag.moved) pick(sim.drag.i);
    else if (sim.pan && !sim.pan.moved && !picking) {
      setSel(null);
      setCmp(null);
      setCluster(null);
      setUserCluster(null);
      setTagFilter([]);
    }
    sim.drag = null;
    sim.pan = null;
  };

  const changeGraph = (fn) => {
    fn();
    setCluster(null);
  };

  const openUserCluster = (id) => {
    setUserCluster(id);
    setCluster(null);
    setTagFilter([]);
    setSel(null);
    setCmp(null);
    setPicking(false);
  };
  const createUserCluster = (name, indices) => {
    const id = userClusterStore.create(name, indices.map((i) => games[i].id));
    openUserCluster(id);
  };
  const currentEmpty = isEmptyView({ sel, cluster, userCluster, tagFilter });
  const backIndex = currentEmpty ? history.index : history.index - 1;
  const forwardIndex = history.index + 1;
  const goToHistory = (index) => {
    const entry = history.entries[index];
    if (!entry) return;
    const v = entry.view;
    setHistory((h) => ({ ...h, index }));
    setSel(v.sel);
    setCmp(v.cmp);
    setCluster(v.cluster);
    setUserCluster(v.userCluster);
    setTagFilter(v.tagFilter);
    setPicking(false);
    setHov(null);
  };

  const openCluster = (id) => {
    setCluster(id);
    setUserCluster(null);
    setTagFilter([]);
    setSel(null);
    setCmp(null);
    setPicking(false);
  };

  const isTagActive = (kind, value) => tagFilter.some((t) => t.kind === kind && t.value === value);
  // From a game, a tag starts a fresh filter; from the filter view, it narrows it.
  const startTagFilter = (kind, value) => {
    setTagFilter([{ kind, value }]);
    setSel(null);
    setCmp(null);
    setCluster(null);
    setUserCluster(null);
    setPicking(false);
    setHov(null);
  };
  const addTagFilter = (kind, value) => {
    if (!isTagActive(kind, value)) setTagFilter((f) => [...f, { kind, value }]);
  };
  const removeTagFilter = (kind, value) =>
    setTagFilter((f) => f.filter((t) => t.kind !== kind || t.value !== value));

  const assignToUserCluster = (i, id) => userClusterStore.assign(games[i].id, id);
  const deleteUserCluster = (id) => {
    const c = userClusters.byId[id];
    if (c && c.size && !window.confirm(`Delete "${c.name}"? Its ${c.size} games stay in the graph.`)) return;
    userClusterStore.remove(id);
  };
  const userClusterActions = {
    userClusters,
    suggestions,
    userClusterStats,
    suggestFromOthers,
    onSuggestFromOthers: setSuggestFromOthers,
    hidden,
    onOpenUserCluster: openUserCluster,
    onCreateUserCluster: createUserCluster,
    onAssignUserCluster: assignToUserCluster,
    onRenameUserCluster: userClusterStore.rename,
    onDeleteUserCluster: deleteUserCluster,
  };

  const q = query.trim().toLowerCase();
  const gameResults = q ? games.filter((g) => g.name.toLowerCase().includes(q)).slice(0, 6) : [];
  const clusterResults = q
    ? [
        ...userClusters.list.map((c) => ({ ...c, mine: true })),
        ...graph.clusters.map((c) => ({ ...c, mine: false })),
      ]
        .filter((c) => c.name.toLowerCase().includes(q))
        .slice(0, 3)
    : [];
  // Tags that start with the query come before ones that only contain it.
  const tagResults = q
    ? allTags
        .filter((t) => t.search.includes(q))
        .sort((a, b) => b.search.startsWith(q) - a.search.startsWith(q) || b.count - a.count)
        .slice(0, 4)
    : [];
  const results = [
    ...gameResults.map((g) => ({ key: `game:${g.id}`, game: g })),
    ...clusterResults.map((c) => ({ key: `${c.mine ? "mine" : "suggested"}:${c.id}`, cluster: c })),
    ...tagResults.map((t) => ({ key: `${t.kind}:${t.value}`, tag: t })),
  ];

  const chooseResult = (r) => {
    setQuery("");
    setActiveResult(0);
    if (r.cluster) {
      if (r.cluster.mine) openUserCluster(r.cluster.id);
      else openCluster(r.cluster.id);
      sim.autoFit = true;
      return;
    }
    if (r.tag) {
      startTagFilter(r.tag.kind, r.tag.value);
      sim.autoFit = true;
      return;
    }
    focusOn(r.game.index);
    pick(r.game.index);
  };

  const onSearchKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveResult((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveResult((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      chooseResult(results[activeResult] || results[0]);
    }
  };

  const shareUrl = `${window.location.origin}${window.location.pathname}#/ludograph?w=${FACTORS.map(
    ({ key }) => weights[key]
  ).join("-")}&s=${seriesWeight}&min=${threshold}`;

  const detailHidden = narrow && sel == null && cluster == null && userCluster == null && !tagFilter.length;

  return (
    <div className="ludograph">
      <header className="nav">
        <div className="nav-brand ludograph-brand">
          Ludograph<span className="ludograph-brand-sub">Your collection</span>
        </div>
        <a href="#/">Game list</a>
        <span className="ludograph-num ludograph-header-stats">
          {n} games · {graph.edges.length} links · {graph.clusters.length} clusters
        </span>
        <button className="btn btn-primary" onClick={() => setSharing(true)}>
          Share
          <svg width="16" height="16" viewBox="0 0 24 24" className="ludograph-icon">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
      </header>

      <div className={`ludograph-body${narrow ? " is-narrow" : ""}`}>
        <WeightPanel
          weights={weights}
          shares={shares}
          onWeight={(key, value) => changeGraph(() => setWeights((w) => ({ ...w, [key]: value })))}
          seriesWeight={seriesWeight}
          onSeriesWeight={(value) => changeGraph(() => setSeriesWeight(value))}
          threshold={threshold}
          onThreshold={(value) => changeGraph(() => setThreshold(value))}
          maxLinks={maxLinks}
          clusters={graph.clusters}
          activeCluster={cluster}
          onCluster={(id) => (cluster === id ? setCluster(null) : openCluster(id))}
          userClusters={userClusters.list}
          activeUserCluster={userCluster}
          onUserCluster={(id) => (userCluster === id ? setUserCluster(null) : openUserCluster(id))}
          onlyUnclustered={onlyUnclustered}
          onOnlyUnclustered={setOnlyUnclustered}
          unclusteredCount={userClusters.clusterOf.filter((c) => c == null).length}
          games={games}
          stats={{
            games: n,
            links: graph.edges.length,
            isolated: graph.strength.filter((x) => x === 0).length,
          }}
        />

        <main className="ludograph-main">
          <div ref={boxRef} className="ludograph-canvas">
            <GraphView
              svgRef={svgRef}
              sim={sim}
              graph={graph}
              games={games}
              sel={sel}
              cmp={cmp}
              hov={hov}
              cluster={cluster}
              userCluster={activeUserCluster}
              tagMatches={tagMatches}
              suggestions={suggestions}
              hidden={hidden}
              path={path}
              panning={Boolean(sim.pan && sim.pan.moved)}
              labels={labels}
              nodeShape={nodeShape}
              onNodeDown={onNodeDown}
              onNodeEnter={(i) => {
                if (!sim.drag && !sim.pan) setHov(i);
              }}
              onNodeLeave={() => setHov(null)}
              onBgDown={onBgDown}
              onMove={onMove}
              onUp={onUp}
            />
          </div>

          <div className="ludograph-search">
            <input
              className="input"
              placeholder="Find a game, cluster or tag"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveResult(0);
              }}
              onKeyDown={onSearchKeyDown}
              autoComplete="off"
              spellCheck="false"
              aria-label="Find a game, cluster, genre, theme or mechanic"
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls="ludograph-results"
              aria-activedescendant={results.length ? `ludograph-result-${activeResult}` : undefined}
            />
            {results.length > 0 && (
              <div className="ludograph-results" id="ludograph-results" role="listbox">
                {results.map((r, i) => (
                  <button
                    key={r.key}
                    id={`ludograph-result-${i}`}
                    role="option"
                    aria-selected={i === activeResult}
                    tabIndex={-1}
                    className={i === activeResult ? "active" : undefined}
                    onMouseEnter={() => setActiveResult(i)}
                    onClick={() => chooseResult(r)}
                  >
                    {r.game ? (
                      <>
                        <span className="ludograph-result-name">{r.game.name}</span>
                        <span className="ludograph-muted ludograph-small">{r.game.year || ""}</span>
                      </>
                    ) : r.cluster ? (
                      <>
                        <span className="ludograph-result-name">{r.cluster.name}</span>
                        <span className="ludograph-muted ludograph-small ludograph-nowrap">
                          {r.cluster.mine ? "Your cluster" : "Suggested"} · {r.cluster.size}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="ludograph-result-name">{r.tag.label}</span>
                        <span className="ludograph-muted ludograph-small ludograph-nowrap">
                          {r.tag.single} · {r.tag.count} {r.tag.count === 1 ? "game" : "games"}
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ludograph-zoom">
            <button className="btn btn-icon" onClick={() => zoom(1.25)} title="Zoom in">
              <svg width="16" height="16" viewBox="0 0 24 24" className="ludograph-icon">
                <path d="M5 12h14M12 5v14" />
              </svg>
            </button>
            <button className="btn btn-icon" onClick={() => zoom(0.8)} title="Zoom out">
              <svg width="16" height="16" viewBox="0 0 24 24" className="ludograph-icon">
                <path d="M5 12h14" />
              </svg>
            </button>
            <button
              className="btn btn-icon"
              onClick={() => {
                sim.autoFit = true;
                rerender();
              }}
              title="Fit to view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" className="ludograph-icon">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          </div>

          {picking ? (
            <div className="ludograph-picking">
              <span>Pick a game to compare with {games[sel].name}</span>
              <button className="btn" onClick={() => setPicking(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="ludograph-hint">Click a game to inspect · drag to move · scroll to zoom</div>
          )}
        </main>

        <DetailPanel
          className={`ludograph-detail${narrow ? " is-drawer" : ""}`}
          style={detailHidden ? { display: "none" } : undefined}
          model={model}
          graph={graph}
          weights={weights}
          shares={shares}
          seriesWeight={seriesWeight}
          sel={sel}
          cmp={cmp}
          path={path}
          onSelect={select}
          onEnter={setHov}
          onLeave={() => setHov(null)}
          onCompare={(o) => {
            setCmp(o);
            setHov(null);
          }}
          onStartPick={() => setPicking(true)}
          onClose={clearSel}
          onSwap={() => {
            setSel(cmp);
            setCmp(sel);
          }}
          onBack={() => setCmp(null)}
          cluster={cluster}
          onCloseCluster={() => setCluster(null)}
          userCluster={activeUserCluster}
          onCloseUserCluster={() => setUserCluster(null)}
          tagFilter={tagFilter}
          canBack={backIndex >= 0}
          canForward={forwardIndex < history.entries.length}
          onHistoryBack={() => goToHistory(backIndex)}
          onHistoryForward={() => goToHistory(forwardIndex)}
          tagMatches={tagMatches}
          onStartTagFilter={startTagFilter}
          onAddTagFilter={addTagFilter}
          onRemoveTagFilter={removeTagFilter}
          onClearTagFilter={() => setTagFilter([])}
          {...userClusterActions}
        />
      </div>

      {sharing && <ShareDialog model={model} graph={graph} url={shareUrl} onClose={() => setSharing(false)} />}
    </div>
  );
};

export default Ludograph;
